import { ReturnedProduct } from "@redotech/customer-portal-sdk/rpc/schema/get-returns-by-order-id";
import { DetailedProductInfo } from "@redotech/customer-portal-sdk/rpc/schema/schemas";
import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter, moneyFormat } from "@redotech/money/util";
import { t } from "@redotech/redo-i18n";
import { RedoProductData } from "@redotech/redo-product-common/product";
import {
  AdvancedExchangeItem,
  NonReturnableItem,
  PendingReturnItem,
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { DEFAULT_VARIANT_TITLE } from "@redotech/redo-return-core-common/draft-return/draft-return-items-constants";
import { ReturnAddress } from "@redotech/redo-return-core-common/return";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { ProductData } from "@redotech/redo-return-operations-common/customer-portal/product";
import {
  DraftRegistration,
  getPrimaryProduct as getPrimaryRegistrationProduct,
  OnlineSourceDetails,
  SourceDetails,
} from "@redotech/redo-return-operations-common/draft-registration";
import {
  Address,
  DraftReturn,
} from "@redotech/redo-return-operations-common/draft-return/draft-return";
import {
  getCountryName,
  getPrimaryProduct,
} from "@redotech/redo-return-operations-common/draft-return/util";
import { isProductVariantInStock } from "@redotech/redo-return-operations-common/returns/products/product-variant-utils";
import { RegistrationRewardType } from "@redotech/redo-return-warranties-common/extended-warranty";
import { PurchaseChannel } from "@redotech/redo-return-warranties-common/warranties";
import { InventoryPolicy } from "@redotech/redo-shopify-order-common/shopify-inventory-policy";
import { LineItem } from "@redotech/redo-shopify-order-common/shopify-order";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { ProductInfo } from "@redotech/shopify-storefront/products";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { CombinedReturnItem } from "./flow/util";
import {
  getStorefrontProductMetafields,
  getStorefrontVariantMetafields,
} from "./hooks/use-variant-picker/storefront-metafields";
import { getVariantPickerColorMetafields } from "./hooks/use-variant-picker/variant-picker-color-metafield";
import { CustomerPortalAppSettings } from "./settings";

export const isInternationalReturn = (
  shipFromCountry: string | undefined,
  shipToCountry: string | undefined,
  settings: CustomerPortalAppSettings,
): boolean => {
  let shipFromCountryName: string | undefined = shipFromCountry;
  if (shipFromCountry?.length == 2) {
    shipFromCountryName = getCountryName(shipFromCountry);
  }
  const shipToCountryName =
    shipToCountry || settings.merchantAddress?.country_name;

  return shipFromCountryName !== shipToCountryName;
};

const normalizeFlowText = (text: string): string =>
  text.replace(/\s+/g, " ").trim();

export const localizeCustomerPortalFlowText = (text: string): string => {
  if (!text) {
    return "";
  }

  switch (normalizeFlowText(text)) {
    case "We do not accept international returns. If you have any questions, please contact customer service":
      return t(
        "We do not accept international returns. If you have any questions, please contact customer service",
      );
    case "This item is outside the return policy window. If you have any questions, please contact customer service":
      return t(
        "This item is outside the return policy window. If you have any questions, please contact customer service",
      );
    case "This is a final sale item.":
      return t("This is a final sale item.");
    case "This item is not eligible for a withdrawal":
      return t("This item is not eligible for a withdrawal");
    case "Please upload an image of the damaged item.":
      return t("Please upload an image of the damaged item.");
    case "Please upload an image of the incorrect item.":
      return t("Please upload an image of the incorrect item.");
    case "This item is not covered by your warranty.":
      return t("This item is not covered by your warranty.");
    case "Please enter in the serial number":
      return t("Please enter in the serial number");
    case "What is the item's serial number?":
      return t("What is the item's serial number?");
    case "Please enter in the location of purchase":
      return t("Please enter in the location of purchase");
    case "Where did you purchase the item?":
      return t("Where did you purchase the item?");
    case "Please enter in the date of purchase":
      return t("Please enter in the date of purchase");
    case "When did you purchase the item?":
      return t("When did you purchase the item?");
    case "Please upload a photo of your order receipt":
      return t("Please upload a photo of your order receipt");
    case "Order receipt":
      return t("Order receipt");
    case "Are you located in the United States?":
      return t("Are you located in the United States?");
    case "Our recycling program is eligible on U.S. orders only. If you have any questions, please contact customer service.":
      return t(
        "Our recycling program is eligible on U.S. orders only. If you have any questions, please contact customer service.",
      );
    case "What is the issue with this item?":
      return t("What is the issue with this item?");
    case "Select items to return":
      return t("Select items to return");
    case "Choose the items that you wish to return.":
      return t("Choose the items that you wish to return.");
    case "Choose the items that you wish to return or exchange.":
      return t("Choose the items that you wish to return or exchange.");
    case "This item has already been submitted for return and was rejected":
      return t(
        "This item has already been submitted for return and was rejected",
      );
    case "This item has already been fully returned":
      return t("This item has already been fully returned");
    case "This item has not been fulfilled":
      return t("This item has not been fulfilled");
    case "This product is tagged as excluded from returns":
      return t("This product is tagged as excluded from returns");
    case "This product is excluded from returns by settings":
      return t("This product is excluded from returns by settings");
    case "This product is in an excluded collection":
      return t("This product is in an excluded collection");
    case "Leave it on your doorstep. We'll handle the rest.":
      return t("Leave it on your doorstep. We'll handle the rest.");
    case "Pack up your return and leave it at your door. We'll take care of it for only %price%.":
      return t(
        "Pack up your return and leave it at your door. We'll take care of it for only %price%.",
      );
    case "Schedule package pickup":
      return t("Schedule package pickup");
    case "Ship back to us":
      return t("Ship back to us");
    case "Item arrived damaged or broken":
      return t("Item arrived damaged or broken");
    case "I never received this item":
      return t("I never received this item");
    case "My item was stolen":
      return t("My item was stolen");
    case "Yes":
      return t("Yes");
    case "No":
      return t("No");
    case "Have you checked with your neighbors, in other locations such as a garage, and with the post office or carrier? Most packages are either nearby or being held for pickup.":
      return t(
        "Have you checked with your neighbors, in other locations such as a garage, and with the post office or carrier? Most packages are either nearby or being held for pickup.",
      );
    case "Please upload images of the damaged item(s), including images of the shipping container.":
      return t(
        "Please upload images of the damaged item(s), including images of the shipping container.",
      );
    case "This order falls outside of our claim window. Claims for merchandise damaged during shipping must be filed within 30 days of delivery.":
      return t(
        "This order falls outside of our claim window. Claims for merchandise damaged during shipping must be filed within 30 days of delivery.",
      );
    case "Please check with your neighbors, in other locations such as a garage, and with the post office or carrier before filing a claim. We wait 7 days after the last delivery update before processing since carriers often update delivery information early.":
      return t(
        "Please check with your neighbors, in other locations such as a garage, and with the post office or carrier before filing a claim. We wait 7 days after the last delivery update before processing since carriers often update delivery information early.",
      );
    case "Please include links to video footage, a description of what happened, and any other information you have on the theft.":
      return t(
        "Please include links to video footage, a description of what happened, and any other information you have on the theft.",
      );
    case "This falls outside of our claim policy window. Claims for stolen merchandise must be submitted no later than 30 days from the time of delivery.":
      return t(
        "This falls outside of our claim policy window. Claims for stolen merchandise must be submitted no later than 30 days from the time of delivery.",
      );
    case "Please provide a description of the damaged items and shipping container.":
      return t(
        "Please provide a description of the damaged items and shipping container.",
      );
    case "What happens next?":
      return t("What happens next?");
    case "We'll review your return request":
      return t("We'll review your return request");
    case "We'll review your claim request":
      return t("We'll review your claim request");
    case "We'll review your warranty claim request":
      return t("We'll review your warranty claim request");
    case "We'll review your recycling request":
      return t("We'll review your recycling request");
    case "We'll review your withdrawal request":
      return t("We'll review your withdrawal request");
    case "We'll email you the return label if your return is approved":
      return t("We'll email you the return label if your return is approved");
    case "We'll email you a label for your items if your claim is approved":
      return t(
        "We'll email you a label for your items if your claim is approved",
      );
    case "We'll email you the label if your warranty claim is approved":
      return t("We'll email you the label if your warranty claim is approved");
    case "We'll email you the label if your recycling request is approved":
      return t(
        "We'll email you the label if your recycling request is approved",
      );
    case "We'll email you the label if your withdrawal is approved":
      return t("We'll email you the label if your withdrawal is approved");
    case "Pack up your returns":
      return t("Pack up your returns");
    case "Pack up your items":
      return t("Pack up your items");
    case "Print your shipping label":
      return t("Print your shipping label");
    case "(or open the QR code)":
      return t("(or open the QR code)");
    case "Show the QR code at the carrier's location":
      return t("Show the QR code at the carrier's location");
    case "Look below for all of your shipping labels":
      return t("Look below for all of your shipping labels");
    case "Carrier delivers the return package to us":
      return t("Carrier delivers the return package to us");
    case "Carrier delivers the package to us":
      return t("Carrier delivers the package to us");
    case "We'll review and process your return":
      return t("We'll review and process your return");
    case "We'll review and process your claim":
      return t("We'll review and process your claim");
    case "We'll review and process your warranty claim request":
      return t("We'll review and process your warranty claim request");
    case "We'll review and process your recycling request":
      return t("We'll review and process your recycling request");
    case "We'll review and process your withdrawal":
      return t("We'll review and process your withdrawal");
    case "We process the repair":
      return t("We process the repair");
    case "If the item(s) is not repairable, we will replace or refund the item":
      return t(
        "If the item(s) is not repairable, we will replace or refund the item",
      );
    case "Package Pickup":
      return t("Package Pickup");
    case "Use original packaging or poly bag":
      return t("Use original packaging or poly bag");
    case "Carrier will pickup the package from the pickup address and deliver the return to us. We'll review and process your return":
      return t(
        "Carrier will pickup the package from the pickup address and deliver the return to us. We'll review and process your return",
      );
    case "Drop off package":
      return t("Drop off package");
    case "Take your package to a nearby":
      return t("Take your package to a nearby");
    case "We process the return":
      return t("We process the return");
    case "We process the withdrawal":
      return t("We process the withdrawal");
    case "We process the claim":
      return t("We process the claim");
    case "Your return submission is almost complete":
      return t("Your return submission is almost complete");
    case "Your claim submission is almost complete":
      return t("Your claim submission is almost complete");
    case "Pay for your exchange to receive your return label and complete your submission.":
      return t(
        "Pay for your exchange to receive your return label and complete your submission.",
      );
    case "Pay for your exchange to receive your return label and complete your claim submission.":
      return t(
        "Pay for your exchange to receive your return label and complete your claim submission.",
      );
    case "Your return has been submitted":
      return t("Your return has been submitted");
    case "Your claim has been submitted":
      return t("Your claim has been submitted");
    case "Your warranty claim has been submitted":
      return t("Your warranty claim has been submitted");
    case "Your recycling request has been submitted":
      return t("Your recycling request has been submitted");
    case "We have received your submission. Your return will be processed soon.":
      return t(
        "We have received your submission. Your return will be processed soon.",
      );
    case "We have received your submission. Your claim will be processed soon.":
      return t(
        "We have received your submission. Your claim will be processed soon.",
      );
    case "We have received your submission. Your warranty claim will be processed soon.":
      return t(
        "We have received your submission. Your warranty claim will be processed soon.",
      );
    case "We have received your submission. Your recycling request will be processed soon.":
      return t(
        "We have received your submission. Your recycling request will be processed soon.",
      );
    case "There were problems submitting your return":
      return t("There were problems submitting your return");
    case "There were problems submitting your claim":
      return t("There were problems submitting your claim");
    case "There were problems submitting your warranty claim":
      return t("There were problems submitting your warranty claim");
    case "There were problems submitting your recycling request":
      return t("There were problems submitting your recycling request");
    case "Your return has been rejected":
      return t("Your return has been rejected");
    case "Your claim has been rejected":
      return t("Your claim has been rejected");
    case "Your warranty claim has been rejected":
      return t("Your warranty claim has been rejected");
    case "Your recycling request has been rejected":
      return t("Your recycling request has been rejected");
    case "How would you like to return this item?":
      return t("How would you like to return this item?");
    case "Select how you would like to proceed.":
      return t("Select how you would like to proceed.");
    case "Review your return":
      return t("Review your return");
    case "Review your shipping claim":
      return t("Review your shipping claim");
    case "Review your warranty claim request":
      return t("Review your warranty claim request");
    case "Review your recycling request":
      return t("Review your recycling request");
    case "Submit return":
      return t("Submit return");
    case "Submit claim":
      return t("Submit claim");
    case "Submit warranty claim request":
      return t("Submit warranty claim request");
    case "Submit recycling request":
      return t("Submit recycling request");
    case "Return value":
      return t("Return value");
    case "New order":
      return t("New order");
    case "Box and ship it back to us":
      return t("Box and ship it back to us");
    case "Print a shipping label and bring it to a carrier":
      return t("Print a shipping label and bring it to a carrier");
    case "Items you're returning:":
      return t("Items you're returning:");
    case "What you're filing a claim for:":
      return t("What you're filing a claim for:");
    case "Items you're submitting:":
      return t("Items you're submitting:");
    case "New items:":
      return t("New items:");
    default:
      return text;
  }
};

export function ReturnAddressToAddress(address: ReturnAddress): Address {
  return {
    name: address.name ?? "",
    email: address.email ?? "",
    street1: address.address1,
    street2: address.address2 || "",
    state: address.province,
    city: address.city,
    country: address.country ?? address.country_code ?? "",
    zip: address.zip ?? "",
    phone: address.phone,
    returnerName: address.name,
    returnerEmail: address.email,
  };
}

/**
 * Formats a price with optional currency conversion based on exchange rate and presentment currency
 */
export const formatPriceWithConversion = (
  price: number,
  currency: Currency,
  presentmentCurrency: string,
  exchangeRate: number,
  locale?: string,
): string => {
  if (exchangeRate > 0) {
    const convertedAmount = price * exchangeRate;
    return moneyFormat(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      { amount: convertedAmount, currency: presentmentCurrency as Currency },
      undefined,
      locale,
    );
  }
  return moneyFormat({ amount: price, currency }, undefined, locale);
};

export const storefrontProductToProductData = (
  product: ProductInfo,
  settings: CustomerPortalAppSettings | undefined,
  returnType: ReturnTypeEnum | undefined,
  locationInventory?: Map<
    string,
    { available: number; inventoryTracked: boolean }
  >,
  isPosReturn?: boolean,
): ProductData => {
  const productId = parseNumericIdFromShopifyId(product.id);
  const productTitle = product.title;
  const productStatus = product.publishedAt === null ? "DRAFT" : "ACTIVE";
  return {
    productId,
    productTitle,
    status: productStatus,
    options: product.options.map((option: any) => ({
      name: option.name,
      values: option.values,
    })),
    images: product.images.nodes.map(
      (image: { url: string; altText?: string | null }) => ({
        url: image.url,
        alt: image.altText?.toString(),
      }),
    ),
    variants: product.variants.nodes.map((variant: any) => {
      // When location inventory is provided, CRDB is the source of truth for
      // both quantity and tracking. Otherwise fall back to Storefront API inference.
      const locationData = locationInventory?.get(variant.id);

      let quantityAvailable: number;
      let inventoryTracked: boolean;
      let posLocationInventoryAvailable: number | undefined;
      // For POS, we need the online channel's tracking status separately
      // since the POS location may have a different tracking policy.
      const onlineInventoryTracked = !(
        variant.availableForSale === true &&
        variant.requiresShipping === true &&
        variant.quantityAvailable <= 0
      );

      if (isPosReturn && locationData) {
        // POS return: locationData is the POS store's inventory.
        // Keep Storefront API total as onlineInventoryAvailable ("to ship"),
        // and surface the POS location count separately ("in store").
        posLocationInventoryAvailable = locationData.available;
        quantityAvailable = variant.quantityAvailable ?? 0;
        inventoryTracked = locationData.inventoryTracked;
      } else if (isPosReturn && locationInventory) {
        // POS but variant absent from CRDB — untracked, treat as in-stock
        posLocationInventoryAvailable = variant.quantityAvailable ?? 0;
        quantityAvailable = variant.quantityAvailable ?? 0;
        inventoryTracked = false;
      } else if (locationData) {
        // CRDB has a global location_inventory row for this variant — it is tracked.
        // available is already 0 if not activated at the filtered locations.
        quantityAvailable = locationData.available;
        inventoryTracked = locationData.inventoryTracked;
      } else if (locationInventory) {
        // Absent from CRDB results entirely — no location_inventory rows globally.
        // Shopify only creates InventoryLevel records for tracked variants, so
        // absence means untracked (unlimited stock).
        quantityAvailable = variant.quantityAvailable ?? 0;
        inventoryTracked = false;
      } else {
        // No location filtering — use Storefront API inference
        quantityAvailable = variant.quantityAvailable ?? 0;
        const inventoryNotTracked =
          variant.availableForSale === true &&
          variant.requiresShipping === true &&
          variant.quantityAvailable <= 0;
        inventoryTracked = !inventoryNotTracked;
      }

      return {
        variantId: parseNumericIdFromShopifyId(variant.id),
        variantTitle: variant.title,
        productId,
        productTitle,
        price: {
          amount: variant.price.amount,
          currency: variant.price.currencyCode,
        },
        compareAtPrice: {
          amount: variant.price?.amount,
          currency: variant.price?.currencyCode,
        },
        image: variant.image ??
          product.featuredImage ?? { url: "", altText: "" },
        options: variant.selectedOptions,
        onlineInventoryAvailable: quantityAvailable,
        posLocationInventoryAvailable,
        isInStock:
          !!settings &&
          (isPosReturn
            ? // POS: in stock if available at the store OR online (merchant can ship).
              // For the online fallback, infer tracking from Storefront API since
              // the POS location's tracking status may differ from online.
              isProductVariantInStock({
                inventorySettings: settings.inventory,
                productStatus,
                inventoryQuantity: posLocationInventoryAvailable ?? 0,
                inventoryTracked,
              }) ||
              isProductVariantInStock({
                inventorySettings: settings.inventory,
                productStatus,
                inventoryQuantity: quantityAvailable ?? undefined,
                inventoryTracked: onlineInventoryTracked,
              })
            : isProductVariantInStock({
                inventorySettings: settings.inventory,
                returnType,
                productStatus,
                inventoryQuantity: quantityAvailable ?? undefined,
                // The Storefront API doesn't expose inventoryPolicy or inventoryTracked.
                // currentlyNotInStock is true when a variant is out of stock but still
                // available for purchase (backorders/overselling), which maps to CONTINUE.
                // When false, the variant is either in stock or out of stock with no
                // overselling, which maps to DENY.
                inventoryPolicy: locationData
                  ? InventoryPolicy.DENY
                  : variant.currentlyNotInStock
                    ? InventoryPolicy.CONTINUE
                    : InventoryPolicy.DENY,
                // The only way to know if a merchant tracks inventory (excluding the edge case above) is to check
                // if it is available for sale AND has a quantity <= 0, if that's true inventory is not tracked.
                // Requires shipping is important to check for online orders.
                inventoryTracked,
              })),
        sku: variant.sku ?? null,
        backorder: variant.backorderDate?.value ?? null,
        metafields: getStorefrontVariantMetafields(
          variant.metafields,
          settings,
        ),
      };
    }),
    productMetafields: getStorefrontProductMetafields(
      product.metafields,
      settings,
    ),
  };
};

export const detailedProductToProductInfo = (
  product: DetailedProductInfo,
): ProductInfo => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return {
    id: product.id,
    title: product.title,
    handle: product.handle,
    vendor: product.vendor,
    onlineStoreUrl: product.onlineStoreUrl,
    totalInventory: product.totalInventory,
    description: product.description,
    productType: product.productType,
    collections: product.collections,
    availableForSale: product.availableForSale,
    tags: product.tags,
    publishedAt: product.publishedAt,
    options: product.options.map((option: any) => ({
      id: option.id,
      name: option.name,
      values: option.values,
    })),
    priceRange: product.priceRange,
    featuredImage: product.featuredImage,
    variants: {
      nodes: product.variants.nodes.map((variant: any) => ({
        id: variant.id,
        requiresShipping: variant.requiresShipping,
        currentlyNotInStock: variant.currentlyNotInStock,
        availableForSale: variant.availableForSale,
        weight: variant.weight,
        weightUnit: variant.weightUnit,
        price: variant.price,
        sku: variant.sku,
        barcode: variant.barcode,
        title: variant.title,
        selectedOptions: variant.selectedOptions,
        quantityAvailable: variant.quantityAvailable,
        image: variant.image,
        backorderDate: variant.backorderDate,
      })),
    },
    images: product.images,
  } as ProductInfo;
};

export enum ItemDetailSize {
  SMALL = "small",
  MEDIUM = "medium",
}

export interface Variant {
  name: string;
  value: string;
}

export interface ReturnItemBadge {
  text: string;
  color: PillTheme;
}

export interface ReturnItemParams {
  id: string;
  pendingReturnItemId: string;
  imageSrc: string;
  title: string;
  subtitle?: string;
  quantity: number;
  variants: Variant[];
  formattedPrice: string;
  isSelected: boolean;
  badgeText?: string;
  size?: ItemDetailSize;
  currency: Currency;
  price: string;
  itemValue?: string | null;
  returnTypeBadges?: ReturnItemBadge[];
  isPreRejected?: boolean;
  rejectMessage?: string;
  rejectElementId?: string;
  isNonReturnable?: boolean;
  nonReturnableReasons?: string;
  isTnplContractLineItem?: boolean;
  presentmentCurrency?: string;
  hideQuantity?: boolean;
  exchangeRate?: number;
  variantId?: string;
  portalDisplayMetafieldValues?: string[];
  collections: string[];
}

/**
 * Break out any items being returned by the quantity & pending return intent, such that
 * we have a separate item for each grouping of quantities being returned of each pending item,
 * and items with the rest (the leftover quantities not being returned)
 *
 * N.B. This isn't the thing that's grouping return items originally,
 * see `consolidateReturnableItems()` for more.
 **/
export function getCombinedReturnItemsGrouped(
  draftReturn?: DraftReturn,
): CombinedReturnItem[] {
  if (!draftReturn) {
    return [];
  }
  const combinedReturnItems: CombinedReturnItem[] = [];
  for (const returnableItem of draftReturn.returnableItems) {
    const associatedPendingItems = draftReturn.pendingReturnItems.filter(
      (pendingItem) =>
        pendingItem.returnableItemId === returnableItem.id &&
        !pendingItem.metadata?.temporary,
    );

    if (associatedPendingItems.length === 0) {
      const primaryProduct = getPrimaryProduct(returnableItem);
      combinedReturnItems.push({
        ...returnableItem,
        availableQuantity: primaryProduct?.quantity ?? 1,
      });
    } else {
      const returnQuantity = associatedPendingItems.reduce(
        (acc, pendingItem) => acc + (pendingItem.quantity ?? 1),
        0,
      );

      const availableQuantity =
        (getPrimaryProduct(returnableItem)?.quantity ?? 1) - returnQuantity;
      for (const pendingItem of associatedPendingItems) {
        combinedReturnItems.push({
          ...returnableItem,
          pendingItem,
          availableQuantity,
        });
      }

      /** The remaining quantity is added as a separate item without any pending items*/
      if (availableQuantity > 0) {
        combinedReturnItems.push({ ...returnableItem, availableQuantity });
      }
    }
  }

  return combinedReturnItems.filter((item) => item.selected);
}

function filterOutDefaultTitleVariants<T>(
  variants: T[] | undefined,
  entryFn: (v: T) => string,
): T[] {
  return (
    variants?.filter(
      (v) =>
        ![DEFAULT_VARIANT_TITLE, "Title", "none"].includes(entryFn(v) ?? ""),
    ) ?? []
  );
}

export function subtitleWithoutDefault(v?: string): string | undefined {
  return v ? filterOutDefaultTitleVariants([v], (v) => v).pop() : undefined;
}

export function variantsWithoutDefault(v?: Variant[]): Variant[] {
  return filterOutDefaultTitleVariants(v, (v) => v.name).filter(
    (v) =>
      !(v.name === "Quantity" && (v.value === "1" || v.value === undefined)),
  );
}

/**
 * Convert return items into arguments usable by <ItemDetail /> and other UI components.
 *
 * Is smart enough to figure out whether to use the primary product, some other product, or a variant exchange if applicable.
 *
 * @param product - if showing the non-primary product. Otherwise use the primary one.
 * @param onlyShowOriginal - if true, display the original item, not the variant exchange item (e.g. for the item detail page).
 * @param returnType - the return type of the draft return
 * @param presentmentCurrency - the presentment currency of the draft return
 * @param exchangeRate - the exchange rate of the draft return
 * Will still use quantity information from the pending return intent if applicable.
 */
export function returnItemToParams({
  item,
  returnType,
  product,
  showBadge,
  onlyShowOriginal,
  isPreRejected,
  rejectMessage,
  rejectElementId,
  presentmentCurrency,
  exchangeRate,
  hideQuantity,
  usePreSelectedReturnValue,
}: {
  item: ReturnableItem | CombinedReturnItem;
  presentmentCurrency: string | null;
  exchangeRate: number | null;
  returnType?: ReturnTypeEnum;
  product?: ReturnProduct;
  showBadge?: boolean;
  onlyShowOriginal?: boolean;
  isPreRejected?: boolean;
  rejectMessage?: string;
  rejectElementId?: string;
  hideQuantity?: boolean;
  usePreSelectedReturnValue?: boolean;
}): ReturnItemParams | undefined {
  const productToUse =
    product ??
    item.products.find((product) => product.id === item.primaryProductId);
  if (!productToUse) {
    return undefined;
  }

  /** Pull some information from the pending return intent if applicable */
  const pendingItem =
    "pendingItem" in item && item.pendingItem ? item.pendingItem : undefined;

  /** (For items on item detail page not associated with a pending return intent) */
  const quantityLeftOver =
    "availableQuantity" in item && item.availableQuantity
      ? item.availableQuantity
      : undefined;

  /** Use the pending quantity intent, or the quantity left over (for items on item detail page not associated with a pending return intent),
   * or the product quantity */
  const quantity =
    pendingItem?.quantity ?? quantityLeftOver ?? productToUse.quantity ?? 1;

  /** If there's a variant exchange, display that and not the product selected options */
  const displayablePendingItem =
    (!onlyShowOriginal && pendingItem?.variantExchangeItem) || undefined;

  const selectedOptions = !displayablePendingItem
    ? variantsWithoutDefault(
        productToUse.variantData.variants.find(
          (variant) => variant.id == productToUse.variantId,
        )?.selectedOptions,
      )
    : [];

  const { primaryBadge, returnTypeBadges } = getReturnItemBadges({
    returnType,
    pendingItem,
    onlyShowOriginal,
    showBadge,
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const currency = Currency[productToUse.currency as keyof typeof Currency];

  const subtitle =
    selectedOptions.length <= 0 && !displayablePendingItem
      ? subtitleWithoutDefault(productToUse.variantTitle)
      : subtitleWithoutDefault(displayablePendingItem?.title);

  return {
    id: item.id,
    pendingReturnItemId: pendingItem?.id ?? "",
    imageSrc: displayablePendingItem?.image ?? productToUse.image,
    title: displayablePendingItem?.productTitle ?? productToUse.title,
    subtitle,
    quantity,
    variants: selectedOptions ?? [],
    formattedPrice: getCurrencyFormatter(
      priceCurrency(presentmentCurrency, currency),
    ).format(+productToUse.price),
    isSelected: item.selected ?? false,
    badgeText: primaryBadge,
    currency,
    price: productToUse.price,
    itemValue:
      (usePreSelectedReturnValue
        ? productToUse.preSelectedReturnValue
        : undefined) ?? productToUse.returnValue,
    returnTypeBadges,
    isPreRejected,
    rejectMessage,
    rejectElementId,
    isTnplContractLineItem: !!item.isTnplContractLineItem,
    exchangeRate: exchangeRate ?? 1,
    presentmentCurrency: presentmentCurrency ?? currency,
    variantId:
      pendingItem?.variantExchangeItem?.variantId ?? productToUse.variantId,
    portalDisplayMetafieldValues: displayablePendingItem
      ? (displayablePendingItem.portalDisplayMetafieldValues ?? undefined)
      : (productToUse.portalDisplayMetafieldValues ?? undefined),
    hideQuantity,
    collections: productToUse.collections,
  };
}

/** Returnable and non-returnable item prices are presentment amounts, while
 * the item's currency is the shop's. */
function priceCurrency(
  presentmentCurrency: string | null,
  shopCurrency: Currency,
): Currency {
  if (!presentmentCurrency) {
    return shopCurrency;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Currency[presentmentCurrency as keyof typeof Currency] ?? shopCurrency;
}

export function nonReturnableItemToParams(
  item: NonReturnableItem,
  presentmentCurrency: string | null,
  exchangeRate?: number,
): ReturnItemParams {
  const currency =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    Currency[item.currency as keyof typeof Currency] || Currency.USD;

  return {
    id: item.lineItemId,
    pendingReturnItemId: "",
    imageSrc: item.image || "",
    title: item.title,
    subtitle: item.variantTitle || undefined,
    quantity: item.quantity,
    variants: [],
    formattedPrice: getCurrencyFormatter(
      priceCurrency(presentmentCurrency, currency),
    ).format(Number(item.price)),
    isSelected: false,
    currency,
    price: item.price,
    itemValue: item.returnValue ?? item.price,
    isNonReturnable: true,
    nonReturnableReasons: item.reasons
      .map((reason) => localizeCustomerPortalFlowText(reason))
      .join(", "),
    presentmentCurrency: presentmentCurrency ?? undefined,
    exchangeRate: exchangeRate ?? 1,
    variantId: item.variantId || undefined,
    collections: [],
  };
}

enum ReturnCompensationBadgeType {
  VARIANT_EXCHANGE = "variant_exchange",
  SIMILAR_ITEM = "similar_item",
  NEW_ITEM = "new_item",
  REFUND = "refund",
  STORE_CREDIT = "store_credit",
  REPAIR = "repair",
}
function getReturnCompensationBadgeTypeText(
  type: ReturnCompensationBadgeType,
): string {
  switch (type) {
    case ReturnCompensationBadgeType.VARIANT_EXCHANGE:
      return t("Variant exchange");
    case ReturnCompensationBadgeType.SIMILAR_ITEM:
      return t("Similar item");
    case ReturnCompensationBadgeType.NEW_ITEM:
      return t("New item");
    case ReturnCompensationBadgeType.REFUND:
      return t("Refund");
    case ReturnCompensationBadgeType.STORE_CREDIT:
      return t("Store credit");
    case ReturnCompensationBadgeType.REPAIR:
      return t("Ready for repair");
  }
}

const returnCompensationBadgeTypeToColor: Record<
  ReturnCompensationBadgeType,
  PillTheme
> = {
  [ReturnCompensationBadgeType.VARIANT_EXCHANGE]: PillTheme.NEUTRAL_BLUE,
  [ReturnCompensationBadgeType.SIMILAR_ITEM]: PillTheme.LIGHT_PINK,
  [ReturnCompensationBadgeType.NEW_ITEM]: PillTheme.SUCCESS,
  [ReturnCompensationBadgeType.REFUND]: PillTheme.NORMAL,
  [ReturnCompensationBadgeType.STORE_CREDIT]: PillTheme.NORMAL,
  [ReturnCompensationBadgeType.REPAIR]: PillTheme.SOLID_LIGHT_BLUE,
};

function getReturnCompensationBadge(
  type: ReturnCompensationBadgeType,
): ReturnItemBadge {
  return {
    text: getReturnCompensationBadgeTypeText(type),
    color: returnCompensationBadgeTypeToColor[type],
  };
}

function determineReturnCompensationBadgeType(
  pendingItem: PendingReturnItem,
): ReturnCompensationBadgeType | undefined {
  // If the pending item has exchange information, then we should use the exchange type badges
  if (pendingItem.variantExchangeItem && !pendingItem.exchangeGroup) {
    return ReturnCompensationBadgeType.VARIANT_EXCHANGE;
  }
  if (pendingItem.exchangeGroup) {
    return ReturnCompensationBadgeType.SIMILAR_ITEM;
  }
  if ((pendingItem.advancedExchangeItems?.length ?? 0) > 0) {
    return ReturnCompensationBadgeType.NEW_ITEM;
  }

  if (
    pendingItem.selectedReturnOption?.method.type ===
    ReturnOptionMethod.REFUND.toString()
  ) {
    return ReturnCompensationBadgeType.REFUND;
  } else if (
    pendingItem.selectedReturnOption?.method.type ===
    ReturnOptionMethod.STORE_CREDIT.toString()
  ) {
    return ReturnCompensationBadgeType.STORE_CREDIT;
  }

  // If there is only one option and it's a repair, then we should use the repair badge
  const onlyOneOption = pendingItem.returnOptions.length === 1;
  const isRepair = pendingItem.returnOptions[0]?.method.type === "repair";
  if (onlyOneOption && isRepair) {
    return ReturnCompensationBadgeType.REPAIR;
  }

  // Otherwise, we should return undefined since it's either store credit, refund, or haven't elected a compensation method yet.
  return undefined;
}

function getReturnTypePrimaryBadge(returnType: ReturnTypeEnum): string {
  switch (returnType) {
    case ReturnTypeEnum.RETURN:
      return t("Ready for return");
    case ReturnTypeEnum.CLAIM:
    case ReturnTypeEnum.WARRANTY:
      return t("Claim ready");
    case ReturnTypeEnum.RECYCLE:
    case ReturnTypeEnum.WITHDRAWAL:
      return t("Ready");
      return t("Ready for withdrawal");
  }
}

function getPrimaryBadge(
  showBadge?: boolean,
  returnType?: ReturnTypeEnum,
  returnCompensationBadgeType?: ReturnCompensationBadgeType,
): string | undefined {
  // If the compensation method is a repair, the compensation badge will replace the primary badge
  if (
    !showBadge ||
    returnCompensationBadgeType === ReturnCompensationBadgeType.REPAIR
  ) {
    return undefined;
  }
  return getReturnTypePrimaryBadge(returnType ?? ReturnTypeEnum.RETURN);
}

/**
 * Generate badge information for a return item
 *
 * @param pendingItem - The pending return item
 * @param onlyShowOriginal - Whether to show the original item (vs exchange)
 * @param showBadge - Whether to show the "Ready for return" badge
 * @returns Object containing primary badge text and return type badges
 */
export function getReturnItemBadges({
  returnType,
  pendingItem,
  onlyShowOriginal,
  showBadge,
}: {
  returnType?: ReturnTypeEnum;
  pendingItem?: PendingReturnItem;
  onlyShowOriginal?: boolean;
  showBadge?: boolean;
}): { primaryBadge?: string; returnTypeBadges: ReturnItemBadge[] } {
  const returnTypeBadges: ReturnItemBadge[] = [];
  const showReturnTypeBadges = onlyShowOriginal && pendingItem;
  let returnCompensationBadgeType: ReturnCompensationBadgeType | undefined;
  if (showReturnTypeBadges) {
    // Add the return reason badge if it exists on the pending item
    if (pendingItem.returnReason) {
      returnTypeBadges.push({
        text: pendingItem.returnReason,
        color: PillTheme.NORMAL,
      });
    }

    returnCompensationBadgeType =
      determineReturnCompensationBadgeType(pendingItem);
    // Add the type of compensation badge
    if (returnCompensationBadgeType) {
      returnTypeBadges.push(
        getReturnCompensationBadge(returnCompensationBadgeType),
      );
    }
  }

  const primaryBadge = getPrimaryBadge(
    showBadge,
    returnType,
    returnCompensationBadgeType,
  );

  return { primaryBadge, returnTypeBadges };
}

export function advancedExchangeItemToParams(
  item: AdvancedExchangeItem,
  exchangeRate: number,
  presentmentCurrency: string,
): ReturnItemParams {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const currency = Currency[item.currencyCode as keyof typeof Currency];

  return {
    id: "",
    pendingReturnItemId: "",
    imageSrc: item?.images?.[0] ?? "",
    title: item.title,
    subtitle: subtitleWithoutDefault(item.variantTitle),
    quantity: item.quantity,
    variants: [],
    formattedPrice: moneyFormat({
      amount: Number(item.priceSet?.presentmentMoney?.amount ?? item.price),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      currency: presentmentCurrency as Currency,
    }),
    isSelected: true,
    badgeText: undefined,
    currency,
    price:
      exchangeRate === 1
        ? (item.priceSet?.presentmentMoney?.amount ?? item.price)
        : item.price,
    itemValue:
      exchangeRate === 1
        ? (item.itemValueSet?.presentmentMoney?.amount ?? item.itemValue)
        : item.itemValue,
    presentmentCurrency,
    exchangeRate,
    variantId: item.variantId,
    collections: [],
    portalDisplayMetafieldValues:
      item.portalDisplayMetafieldValues ?? undefined,
  };
}

export function registrationProductToParams(
  product: ReturnProduct,
): ReturnItemParams | undefined {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const currency = Currency[product.currency as keyof typeof Currency];
  return {
    id: product.id,
    pendingReturnItemId: "",
    imageSrc: product.image,
    title: product.title,
    subtitle: subtitleWithoutDefault(product.variantTitle),
    quantity: product.quantity ?? 1,
    variants: [],
    formattedPrice: getCurrencyFormatter(currency).format(
      Number(product.price),
    ),
    isSelected: true,
    badgeText: undefined,
    currency,
    price: product.price,
    itemValue: product.returnValue,
    collections: product.collections,
  };
}

export function shopifyLineItemToReturnItemParams(
  lineItem: LineItem,
): ReturnItemParams {
  const currency =
    Currency[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      lineItem.price_set?.presentment_money
        ?.currency_code as keyof typeof Currency
    ];

  const presentmentCurrency =
    lineItem.price_set?.presentment_money?.currency_code;

  return {
    id: lineItem.id?.toString() ?? "",
    pendingReturnItemId: "",
    imageSrc: lineItem.image?.src ?? "",
    title: lineItem.title,
    subtitle: lineItem.variant_title ?? undefined,
    quantity: lineItem.quantity,
    variants: [],
    formattedPrice: getCurrencyFormatter(currency).format(
      Number(lineItem.price),
    ),
    isSelected: true,
    badgeText: undefined,
    currency,
    price: lineItem.price,
    itemValue: lineItem.price,
    presentmentCurrency,
    exchangeRate: getExchangeRateAtTimeOfOrder([lineItem]),
    collections: [],
  };
}

export function returnableItemToReturnItemParams(
  returnableItem: ReturnableItem,
  presentmentCurrency: string,
  exchangeRate: number,
  locale?: string,
): ReturnItemParams {
  const primaryProduct = getPrimaryProduct(returnableItem);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const currencyCode = (primaryProduct?.currency as Currency) ?? Currency.USD;

  return {
    id: returnableItem.id,
    pendingReturnItemId: "",
    imageSrc:
      primaryProduct?.image ?? "https://assets.getredo.com/product-image.png",
    title: primaryProduct?.title ?? "",
    subtitle: subtitleWithoutDefault(primaryProduct?.variantTitle),
    quantity: primaryProduct?.quantity ?? 1,
    variants: [],
    formattedPrice: formatPriceWithConversion(
      Number(primaryProduct?.returnValue),
      currencyCode,
      presentmentCurrency,
      exchangeRate,
      locale,
    ),
    isSelected: true,
    currency: currencyCode,
    price: primaryProduct?.price,
    itemValue: primaryProduct?.returnValue,
    presentmentCurrency,
    exchangeRate,
    collections: primaryProduct?.collections ?? [],
  };
}

export function returnedProductToReturnItemParams(
  returnedProduct: ReturnedProduct,
  currency: Currency,
  presentmentCurrency: string,
  exchangeRate: number,
  locale?: string,
): ReturnItemParams {
  return {
    id: returnedProduct._id,
    pendingReturnItemId: "",
    imageSrc:
      returnedProduct.images?.[0] ??
      "https://assets.getredo.com/product-image.png",
    title: returnedProduct.product_title,
    subtitle: subtitleWithoutDefault(returnedProduct.variant_title),
    quantity: returnedProduct.quantity ?? 1,
    variants: [],
    formattedPrice: formatPriceWithConversion(
      Number(returnedProduct.price),
      currency,
      presentmentCurrency,
      exchangeRate,
      locale,
    ),
    isSelected: true,
    currency,
    price: returnedProduct.price,
    itemValue: returnedProduct.price,
    presentmentCurrency,
    exchangeRate,
    collections: [],
  };
}

function onlineHasExtendedWarranty(
  source: OnlineSourceDetails | undefined,
  variantId: string,
): boolean {
  const lineItem = source?.eligibleLineItems?.find(
    (li) => String(li.variant_id) === variantId,
  );
  const existingExtendedWarranty = source?.existingExtendedWarranties?.find(
    (ew) => ew.variantId === variantId,
  );
  const lineItemQuantity = lineItem?.originalLineItemQuantity;
  const existingExtendedWarrantyQuantity = existingExtendedWarranty?.quantity;
  return !!(
    lineItemQuantity &&
    existingExtendedWarrantyQuantity &&
    lineItemQuantity <= existingExtendedWarrantyQuantity
  );
}
function hasExtendedWarranty(
  source: SourceDetails | undefined,
  variantId: string,
): boolean {
  if (!source) {
    return false;
  }
  const purchaseChannel = source.purchaseChannel;
  switch (purchaseChannel) {
    case PurchaseChannel.ONLINE:
      return onlineHasExtendedWarranty(source, variantId);
    case PurchaseChannel.RETAIL:
      return false;
  }
}
export function shouldAddEwUpsell(
  draftRegistration: DraftRegistration | undefined,
  settings: CustomerPortalAppSettings | undefined,
): boolean {
  const purchaseChannel =
    draftRegistration?.source.purchaseChannel ?? PurchaseChannel.RETAIL;
  const eligibleUpsellTypes = settings?.warranties?.eligibleUpsellTypes ?? [
    PurchaseChannel.RETAIL,
  ];
  const eligibleForType = eligibleUpsellTypes.includes(purchaseChannel);
  const primaryProduct = getPrimaryRegistrationProduct(draftRegistration);
  const hasExtendedWarranty_ = hasExtendedWarranty(
    draftRegistration?.source,
    primaryProduct?.variantId ?? "",
  );
  return !!(
    settings?.extendedWarranties?.enabled &&
    settings?.extendedWarranties?.upsellEnabled &&
    eligibleForType &&
    !hasExtendedWarranty_
  );
}

export function shouldAddEmailOptInUpsell(
  draftRegistration: DraftRegistration | undefined,
  settings: CustomerPortalAppSettings | undefined,
): boolean {
  return !!(
    settings?.warranties?.registrationRewardType ===
      RegistrationRewardType.EMAIL_OPT_IN_PRODUCT_DISCOUNT &&
    draftRegistration?.userInfo?.emailOptIn === false
  );
}

export const shopifyGraphQLProductToProductData = (
  product: RedoProductData,
  settings: CustomerPortalAppSettings | undefined,
  returnType: ReturnTypeEnum,
): ProductData => {
  return {
    productId: product.id,
    productTitle: product.title,
    status: product.status,
    options: product.options.map((option: any) => ({
      name: option.name,
      values: option.optionValues.map((value: any) => value.name),
    })),
    images: product.images.nodes
      .filter((image) => image.url)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map((image) => ({ url: image.url!, alt: undefined })),
    variants: product.variants.nodes.map((variant: any) => ({
      variantId: variant.id,
      productId: variant.product.id,
      variantTitle: variant.title,
      productTitle: variant.product.title,
      price: {
        amount: variant.contextualPricing.price.amount,
        currency: variant.contextualPricing.price.currencyCode,
      },
      compareAtPrice: {
        amount: variant.contextualPricing.price.amount,
        currency: variant.contextualPricing.price.currencyCode,
      },
      image: variant.image ??
        product.featuredMedia?.preview?.image ?? { url: "", altText: "" },
      options: variant.selectedOptions,
      onlineInventoryAvailable: variant.inventoryQuantity ?? 0,
      isInStock:
        !!settings &&
        isProductVariantInStock({
          inventorySettings: settings.inventory,
          returnType,
          productStatus: product.status,
          inventoryQuantity: variant.inventoryQuantity ?? undefined,
          inventoryPolicy: variant.inventoryPolicy,
          inventoryTracked: variant.inventoryTracked,
        }),
      sku: variant.sku ?? null,
      backorder: variant.backorderDate?.value ?? null,
      metafields: getVariantPickerColorMetafields(
        variant.metafields,
        settings?.variantPickerColorHexMetafield,
      ),
    })),
  };
};

export const getExchangeRateAtTimeOfOrder = (
  lineItems: LineItem[] | undefined,
): number | undefined => {
  if (!lineItems || lineItems.length === 0) {
    return undefined;
  }

  // Find the line item with the highest presentment amount as this will get the most accurate exchange rate
  let highestPresentmentAmount = 0;
  let highestShopAmount = 0;

  for (const lineItem of lineItems) {
    const presentmentAmount = Number(
      lineItem.price_set?.presentment_money?.amount ?? 0,
    );
    const shopAmount = Number(lineItem.price_set?.shop_money?.amount ?? 0);
    if (
      presentmentAmount <= 0 ||
      shopAmount <= 0 ||
      Number.isNaN(presentmentAmount) ||
      Number.isNaN(shopAmount)
    ) {
      continue;
    }

    if (presentmentAmount > highestPresentmentAmount) {
      highestPresentmentAmount = presentmentAmount;
      highestShopAmount = shopAmount;
    }
  }

  if (highestPresentmentAmount === 0 || highestShopAmount === 0) {
    return undefined;
  }

  return highestPresentmentAmount / highestShopAmount;
};

export const PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP = "packagePickupUpsell";

export interface PriceSubtitleParams {
  variantPrice: number | string | undefined;
  remainingReturnValue: number | undefined;
  isVariantExchange: boolean;
  exchangeRate: number | undefined;
  presentmentCurrency: Currency | undefined;
  originalItemPrice: number | undefined;
  hideStrikethrough?: boolean;
  exchangeOptionsPreviewShowOriginalPrice?: boolean;
  /** Display only. Applied on the draft return invoice, never in cart math. */
  storeCreditBonus?: number;
}

export interface PriceSubtitleResult {
  price: string;
  strikethroughPrice?: string;
  badgeText?: string;
}

export function getPriceSubtitle({
  variantPrice,
  remainingReturnValue,
  isVariantExchange,
  exchangeRate,
  presentmentCurrency,
  originalItemPrice,
  hideStrikethrough,
  exchangeOptionsPreviewShowOriginalPrice,
  storeCreditBonus = 0,
}: PriceSubtitleParams): PriceSubtitleResult | undefined {
  if (variantPrice === undefined || remainingReturnValue === undefined) {
    return undefined;
  }

  const currency = presentmentCurrency ?? Currency.USD;

  // TODO: @devineastmond once returnValue is only presentment, remove this exchange rate conversion (but leave the Math.max)
  const adjustedRemainingReturnValue =
    Math.max(0, remainingReturnValue * (exchangeRate ?? 1)) + storeCreditBonus;

  const strikeThroughValue =
    getCurrencyFormatter(currency).format(+variantPrice);

  if (exchangeOptionsPreviewShowOriginalPrice) {
    return { price: strikeThroughValue };
  }

  if (isVariantExchange) {
    if (adjustedRemainingReturnValue >= +variantPrice) {
      return { price: getCurrencyFormatter(currency).format(0) };
    }

    const adjustedOriginalItemPrice =
      originalItemPrice !== undefined
        ? originalItemPrice * (exchangeRate ?? 1)
        : undefined;

    const addedValue =
      adjustedOriginalItemPrice !== undefined
        ? Math.max(0, +variantPrice - adjustedOriginalItemPrice)
        : Math.max(0, +variantPrice - adjustedRemainingReturnValue);

    return {
      price: getCurrencyFormatter(currency).format(0),
      ...(!hideStrikethrough && { strikethroughPrice: strikeThroughValue }),
      ...(addedValue > 0 && {
        badgeText: t("{{amount}} added value", {
          amount: getCurrencyFormatter(currency).format(addedValue),
        }),
      }),
    };
  }

  const newValue = getCurrencyFormatter(currency).format(
    Math.max(0, +variantPrice - adjustedRemainingReturnValue),
  );

  if (newValue === strikeThroughValue || hideStrikethrough) {
    return { price: newValue };
  }

  return { price: newValue, strikethroughPrice: strikeThroughValue };
}
